import { globals } from 'config/globals';
import { Core } from 'core/Core';
import { device } from 'utils/device';
import { getCoverSize } from 'utils/math';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { ZoomConfig, Size } from './WorkTilePixi';

import { Inertia } from 'utils/motion/Inertia';
import ProjectHeaderSizes from 'app/views/project/ProjectHeaderSizes';
import { geometry } from 'app/contracts/Geometry';
import Config from 'config/settings';
import SiteTransitions from 'app/services/SiteTransitions';

export interface WorkTileImageConfig {
	maskPath?: string;
	scaleStart?: number;
	scaleEnd?: number;
	startScaleRange?: number;
	endScaleRange?: number;
	alphaStart?: number;
	alphaEnd?: number;
	startAlphaRange?: number;
	endAlphaRange?: number;
	tint?: number;
}


interface WorkTileImagePixiTextures {
	image: PIXI.Texture;
	mask: PIXI.Texture;
	bevel: PIXI.Texture;
	black: PIXI.Texture;
	glow: PIXI.Texture;
}

export class WorkTileImagePixi {
	public debug: boolean = false;

	private _textures: WorkTileImagePixiTextures;

	private _$innerContainer: PIXI.Container;
	private _$floatingMaskContainer: PIXI.Container;

	private _$image: PIXI.Sprite;
	private _$glow: PIXI.Sprite;
	private _$blackout: PIXI.Graphics;
	private _$imageEffect: PIXI.Sprite; //Effect copy of the image, faded by progress

	private _$mask: PIXI.Graphics;
	private _$floatingMask: PIXI.Graphics;
	//Write-only state (assigned but never read anywhere — public so noUnusedLocals
	//doesn't flag them; candidates for removal in a runtime-change pass):
	public _tint: string;
	public _mouseDownScale: number;
	public _containerSize: Size;
	private _alpha: number;
	private _scale: number;
	private _displayScale: number = 1;
	private _processedProgress: number;
	private _availbleMaskSize: Size;
	private _fullRange: number = 0.2; //The amount the image is at 100% for

	private _mouse: MouseController;
	private _xParallax: Inertia;
	private _yParallax: Inertia;
	private _mouseOverScaleInertia: Inertia;

	private _backgroundAnchor: number;

	private _mousePallaxMultiplier: number = 0;

	private _onProject = false;

	private _borderRadiusSize = 4;
	private _borderRadius = this._borderRadiusSize;

	private _lastMaskSizes = {
		w: 0,
		h: 0,
		x: 0,
		y: 0
	};

	private _activeProjectBlackoutAlpha: number;
	private _activeProjectY: number;

	constructor(
		_$pixi: PIXI.Application,
		private _$container: PIXI.Container,
		private _config: WorkTileImageConfig,
		backgroundAnchor: number = 0.5
	) {
		this._backgroundAnchor = backgroundAnchor;

		this._config.scaleStart = 1;

		this._config.scaleEnd = 1;
		this._config.alphaStart = 0.01;
		this._config.alphaEnd = 0.01;
		this._config.startScaleRange = 1.0 - this._config.scaleStart;
		this._config.endScaleRange = 1.0 - this._config.scaleEnd;
		this._config.startAlphaRange = 1.0 - this._config.alphaStart;
		this._config.endAlphaRange = 1.0 - this._config.alphaEnd;

		this._mouse = Core.controller.get('Mouse') as MouseController;

		this._xParallax = new Inertia(2, 0);
		this._yParallax = new Inertia(2, 0);
		this._mouseOverScaleInertia = new Inertia(8, 0);
	}

	public get innerContainer(): PIXI.Container {
		return this._$innerContainer;
	}

	public get floatingMaskContainer(): PIXI.Container {
		return this._$floatingMaskContainer;
	}

	public get blackOutAlpha(): number {
		return 1.0 - this._alpha;
	}

	public get computedScale(): number {
		return (1 / this._scale) * this._displayScale;
	}

	//Set the glow tint. The project data supplies a hex colour STRING
	//('0xe169ff' — see ProjectConfig.tint); _tint is write-only anyway:
	public setTint(tint: string): void {
		this._tint = tint;
	}

	//Set required textures:
	public setTextures(textures: WorkTileImagePixiTextures): void {
		this._textures = textures;
	}

	//All dependies set so create the image:
	public create(): void {
		this._createMaskedImage();
		this._createTintTweens();
	}

	public scroll(y) {
	}

	public exitScroll(y) {
		let ratio = 0.5;
		this._$innerContainer.y = this._activeProjectY - y * ratio;
	}

	public startExitScroll(maskStyle) {
		if (maskStyle) {
			this.scroll(0);
		}
	}

	public hideForProject() {
	}

	public showForWork() {
		this._onProject = false;
	}

	public resizeMask() {
		this._redrawMask();
	}

	//Update the item ro animate
	//Rotation of image is determined by velocity of movement and so passed in:
	public update(
		progress: number,
		rotation: number = 0.0,
		skipPixiUpdate: boolean = false,
		isClickable: boolean,
		velocity: number
	): void {


		if (this._onProject) {
			this._positionImages();

			return;
		}

		let zeroBased = Math.abs(1 - progress * 2);
		this._$imageEffect.alpha = zeroBased;


		let progressOrg = progress;
		if (progress < 0.5 - this._fullRange / 2) {
			this._processedProgress = progress / (0.5 - this._fullRange / 2);


			this._scale = this._config.scaleStart + this._config.startScaleRange * this._processedProgress;
			this._alpha = this._config.alphaStart + this._config.startAlphaRange * this._processedProgress;
		} else if (progress > 0.5 + this._fullRange / 2) {
			progress -= 0.5 + this._fullRange / 2;
			this._processedProgress = progress / (0.5 + this._fullRange / 2);
			this._scale = 1.0 - this._processedProgress * this._config.endScaleRange;
			this._alpha = 1.0 - this._processedProgress * this._config.endAlphaRange;
		} else {
			this._scale = 1.0;
			this._alpha = 1.0;
		}


		if (this._alpha < Config.minBlackoutAlpha) {
			this._alpha = Config.minBlackoutAlpha;
		}

		if (skipPixiUpdate) {
			return;
		}

		this._setDisplayScale();

		this._mouseOverScaleInertia.target = !globals.lastCordsFromTouch && isClickable ? -0.02 : 0.0;
		this._mouseOverScaleInertia.tick();
		this._scale += this._mouseOverScaleInertia.value;

		//Set the state of the image:

		this._$blackout.alpha = 1.0 - this._alpha;

		velocity = 0;
		this._$innerContainer.scale.set(
			this._scale *
				(this._displayScale + Math.abs(velocity * 0), this._scale * (this._displayScale + Math.abs(velocity * 0)))
		);

		this._$mask.scale.set(this._scale, this._scale + Math.abs(velocity * 30));
		this._$floatingMask.scale.set(this._scale, this._scale + Math.abs(velocity * 30));
		this._$blackout.scale.set(this._scale, this._scale + Math.abs(velocity * 30));

		this._positionImages();

		this._setImageYAnchor();
		this._setImageParallax(progressOrg);

	}

	//Zoom into project, save the tweens so they can reversved
	public zoomToProject(instant: boolean = false, config: ZoomConfig, delay: number = 0.0): void {
		this._resetParallax();
		this._activeProjectY = config.imageY;

		let tweenTime: number = instant ? 0.0 : Config.toProjectTime * Config.toProjectMultipler;
		delay = 0;

		let yPosition = this._getImageAnchor(
			config.imageScale,
			this._$image.texture.height,
			1,
			ProjectHeaderSizes.headerHeight()
		);

		SiteTransitions.tweens.workTileImage = gsap.TweenMax.to(this._$image, tweenTime * SiteTransitions.speedScale, {
			pixi: { x: 0, y: yPosition, scaleX: 1, scaleY: 1 },
			ease: Config.toProjectEase,
			delay: delay,
			onComplete: () => {
				SiteTransitions.tweens.workTileImage = null;
			}
		});

		let radiusTween = {
			value: this._borderRadius
		};

		SiteTransitions.tweens.workTileImageRadius = gsap.TweenMax.to(radiusTween, tweenTime * SiteTransitions.speedScale, {
			value: 0,
			ease: Config.linearEase,
			delay: delay,
			onUpdate: () => {
				this._borderRadius = Math.round(radiusTween.value);
				this._redrawMask();
			},
			onComplete: () => {
				SiteTransitions.tweens.workTileImageRadius = null;
			}
		});

		SiteTransitions.tweens.workTileMask = gsap.TweenMax.to(
			[this._$mask, this._$floatingMask, this._$blackout],
			tweenTime * SiteTransitions.speedScale,
			{
				ease: Config.toProjectEase,
				pixi: { scaleX: config.maskScaleX, scaleY: config.maskScaleY },
				delay: delay,
				onComplete: () => {
					SiteTransitions.tweens.workTileMask = null;
				}
			}
		);

		SiteTransitions.tweens.workTileSupportElements = gsap.TweenMax.to(
			[this._$innerContainer],
			tweenTime * SiteTransitions.speedScale,
			{
				x: config.imageX,
				y: config.imageY,
				ease: Config.toProjectEase,
				delay: delay,
				pixi: { scaleX: config.imageScale, scaleY: config.imageScale },
				onComplete: () => {
					SiteTransitions.tweens.workTileSupportElements = null;
				}
			}
		);

		this._activeProjectBlackoutAlpha = this._$blackout.alpha;
		SiteTransitions.tweens.workTileBlackout = gsap.TweenMax.to(
			this._$blackout,
			tweenTime * SiteTransitions.speedScale,
			{
				alpha: 0,
				onComplete: () => {
					SiteTransitions.tweens.workTileBlackout = null;
				}
			}
		);
	}

	//Exit out of a project
	public zoomFromProject(): void {
		this._mouseOverScaleInertia.target = this._mouseOverScaleInertia.value = 0;
		this._xParallax.value = this._xParallax.target = 0;
		this._yParallax.value = this._yParallax.target = 0;

		//Reset saved scales to pre-hover amounts, use availble mask size as it is the pre-hover amount:
		this._setDisplayScale(this._availbleMaskSize);
		this._scale = 1;

		//Tweening to size intially then will switch when tick() starts again:
		let realDisplayScale = (1 / this._scale) * this._displayScale;

		let imageScale = this._getImageScale();

		let yPosition = this._getImageAnchor(
			imageScale.h,
			this._$image.texture.height,
			realDisplayScale,
			this._availbleMaskSize.height
		);

		SiteTransitions.tweens.workTileImage = gsap.TweenMax.to(
			this._$image,
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				pixi: { scaleX: imageScale.w, y: yPosition, scaleY: imageScale.h },
				ease: Config.fromProjectEase,
				onComplete: () => {
					SiteTransitions.tweens.workTileImage = null;
				}
			}
		);

		let radiusTween = {
			value: 0
		};

		SiteTransitions.tweens.workTileImageRadius = gsap.TweenMax.to(
			radiusTween,
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				value: this._borderRadiusSize,
				ease: Config.linearEase,
				onUpdate: () => {
					this._borderRadius = Math.round(radiusTween.value);
					this._redrawMask();
				},
				onComplete: () => {
					SiteTransitions.tweens.workTileImageRadius = null;
				}
			}
		);

		SiteTransitions.tweens.workTileMask = gsap.TweenMax.to(
			[this._$mask, this._$floatingMask, this._$blackout],
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				pixi: { scaleX: 1, scaleY: 1 },
				ease: Config.fromProjectEase,
				onComplete: () => SiteTransitions.tweens.workTileMask
			}
		);

		SiteTransitions.tweens.workTileSupportElements = gsap.TweenMax.to(
			this._$innerContainer,
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				pixi: { scaleX: realDisplayScale, scaleY: realDisplayScale, y: this._activeProjectY },
				ease: Config.fromProjectEase,
				onComplete: () => {
					SiteTransitions.tweens.workTileSupportElements = null;
				}
			}
		);

		SiteTransitions.tweens.workTileBlackout = gsap.TweenMax.to(
			this._$blackout,
			Config.fromProjectTime * Config.fromProjectMultipler * SiteTransitions.speedScale,
			{
				alpha: this._activeProjectBlackoutAlpha,
				onComplete: () => {
					SiteTransitions.tweens.workTileBlackout = null;
				}
			}
		);

		//Ease the image parallax back in.
		//This stops it juddering after the mouse interaction is applied again
		this._xParallax.ease = this._yParallax.ease = 100;
		this._xParallax.easeTarget = this._yParallax.easeTarget = 2;
		this._xParallax.easeFrameUpdate = this._yParallax.easeFrameUpdate = 1.6;
	}

	//Get the native image sizes:
	public getImageSizes(): Size {
		return {
			width: this._$image.texture.width,
			height: this._$image.texture.height
		};
	}

	//Get the native mask sizes:
	public getMaskSizes(): Size {
		return {
			width: this._$mask.width,
			height: this._$mask.height
		};
	}

	//Get the display scale (Used to scale particle logic to keep relative to the main image):
	public getDisplayScale(): number {
		return this._displayScale;
	}

	public getActualScale(): number {
		return this._scale * this._displayScale;
	}

	public setMouseDownScale(ratio: number) {
		this._mouseDownScale = ratio * 0.25;
	}

	//Set the aviable mask size on resize:
	public resize(availbleMaskSize: Size, containerSize: Size, skipChanges: boolean = false): void {
		this._availbleMaskSize = availbleMaskSize;
		this._containerSize = containerSize;
		this._setDisplayScale();

		if (skipChanges) {
			return;
		}

		this._positionImages();
		this._setContainerPivot();
	}

	public setProjectActive(projectActive: boolean) {
		let ob = { value: this._mousePallaxMultiplier };
		let target = projectActive ? 1 : 0;

		gsap.TweenMax.to(ob, 1, {
			ease: Config.linearEase,
			value: target,
			onUpdate: () => {
				this._mousePallaxMultiplier = ob.value;
			}
		});
	}

	//Private
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------
	//--------------------------------------------------------------------------------------------

	//Add the masked image to the container:
	private _createMaskedImage(): void {
		if (!this._$container) {
			console.warn('Could not create PIXI MAsked Image because this._$container is not set.');
			return;
		}

		//Note: anchors 0.5 / 0.5  are set in spritesheet / texture packer for all 'particle' items
		this._$innerContainer = new PIXI.Container();

		this._$innerContainer.name = 'innerContainer';

		this._$floatingMaskContainer = new PIXI.Container();

		this._$floatingMaskContainer.name = 'floating mask container';


		//Create mask
		//Drawn in redraw process:
		this._$mask = new PIXI.Graphics();
		this._$mask.name = 'maskname';

		this._$floatingMask = new PIXI.Graphics();
		this._$floatingMask.name = 'floating-mask';

		this._$blackout = new PIXI.Graphics();
		this._$blackout.name = 'blackout';

		//No cacheAsBitmap here (v5 relic): inert on Pixi 8.0-8.5, but the 8.6+
		//deprecation shim re-activates it as cacheAsTexture, which snapshots
		//the sprite while the tile is still hidden during the home->work SPA
		//transition — a permanently black cache (the blank-work-tiles bug).
		this._$image = new PIXI.Sprite(this._textures.image);
		this._$image.name = 'image';
		this._$image.anchor.set(0.5, 0.5);


		this._$imageEffect = new PIXI.Sprite(this._textures.image);
		this._$imageEffect.name = 'imageEffect';
		this._$imageEffect.anchor.set(0.5, 0.5);

		this._$innerContainer.mask = this._$mask;

		this._$floatingMaskContainer.mask = this._$floatingMask;


		this._$glow = new PIXI.Sprite(this._textures.glow);
		this._$glow.name = 'glow';
		this._$glow.anchor.set(0.5, 0.5);

		this._$container.addChild(
			this._$glow,
			this._$innerContainer,
			this._$floatingMaskContainer,
			this._$blackout,
			this._$mask,
			this._$floatingMask
		);

		this._$innerContainer.addChild(this._$image);

		//Scale and size image as needed
		this._setDisplayScale();
		this._positionImages();

	}

	private _createTintTweens(): void {
	}

	//Set the pivot of the image/mask/dropshadow container
	private _setContainerPivot(): void {
		if (!this._$innerContainer) {
			return;
		}

		this._$innerContainer.getBounds();
	}

	//Scale the scaling so the image scaling will stay in sync
	private _setDisplayScale(maskSize: Size = null): void {
		if (!maskSize) {
			maskSize = this.getMaskSizes();
		}

		if (this.debug) {
		}

		if (!maskSize.width || !this._availbleMaskSize) {
			return;
		}



		let imageSizeCover = getCoverSize(ProjectHeaderSizes.videoSize(), maskSize);

		this._displayScale = imageSizeCover.width / 1920;
		this._displayScale *= 1.2;


	}

	private _redrawMask(): void {
		let width = this._availbleMaskSize.width;
		let height = this._availbleMaskSize.height;
		let baseX = Math.floor(-1 * (width / 2));
		let baseY = Math.floor(-1 * (height / 2));


		this._lastMaskSizes.w = width;
		this._lastMaskSizes.h = height;
		this._lastMaskSizes.x = baseX;
		this._lastMaskSizes.y = baseY;


		this._$mask.clear();
		this._$floatingMask.clear();
		this._$blackout.clear();

		this._$mask
			.roundRect(baseX, baseY, width, height, this._borderRadius)
			.fill(0xff0000);

		this._$floatingMask
			.roundRect(baseX, baseY, width, height, this._borderRadius)
			.fill(0xff0000);

		this._$blackout
			.roundRect(baseX, baseY, width, height, this._borderRadius)
			.fill('#000000');
	}

	//Position element in the middle of the area available for the mask on the Tile:
	private _positionImages(): void {
		if (!this._availbleMaskSize) {
			return;
		}

		let x = this._availbleMaskSize.width / 2;
		let y = this._availbleMaskSize.height / 2;
		this._$blackout.x = this._$mask.x = this._$floatingMask.x = this._$glow.x = x;
		this._$blackout.y = this._$mask.y = this._$floatingMask.y = this._$glow.y = y;
		this._$innerContainer.x = x;
		this._$innerContainer.y = y;

		this._redrawMask();

		this._$image.x = 0;
		this._$image.y = 0;
		this._$imageEffect.x = 0;
		this._$imageEffect.y = 0;


		let imageScale = this._getImageScale();
		this._$image.scale.set(imageScale.h, imageScale.w);
		this._$imageEffect.scale.set(imageScale.h, imageScale.w);

		this._setGlowSize();
	}

	private _setGlowSize(): void {
		let s = this._availbleMaskSize.width / this._$glow.texture.width;
		s *= 1.2;
		this._$glow.scale.set(s, s);
	}

	private _getImageScale(): geometry.Size {
		if (device.isPortraitDevice) {
			return {
				h: 0.6,
				w: 0.6
			};
		}

		return {
			h: 0.85,
			w: 0.85
		};
	}

	//When traveling to projetc reset the parallax as it will cause jumping!
	private _resetParallax(): void {
		this._xParallax.target = this._xParallax.value = 0;
		this._yParallax.target = this._yParallax.value = 0;
	}

	/**
	 * Anchor image
	 */
	private _setImageYAnchor(): void {
		//Work out the real scale of things after transforms on parents + image
		let imageScale = this._$image.scale.y;
		let imageHeight = this._$image.texture.height;

		this._$image.y = this._getImageAnchor(imageScale, imageHeight, this._displayScale, this._availbleMaskSize.height);

		this._$imageEffect.y = this._$image.y;
	}

	private _getImageAnchor(imageYScale, imageHeight, displayScale, availableMaskHeight) {
		let workingScale = imageYScale * displayScale;
		let realDisplayHeight = imageHeight * workingScale;

		//Normalsie from -1 to 1. WHen 0.5 is 0
		let normalisedAnchor = this._backgroundAnchor * 2 - 1;
		let availableMovableDistance = realDisplayHeight - availableMaskHeight;
		return (availableMovableDistance / 2) * (-1 * normalisedAnchor);
	}

	private _getImageXPadding(): number {
		let imageScale = this._$image.scale.y;
		let imageWidth = this._$image.texture.width;
		let workingScale = imageScale * this._displayScale;
		let realDisplayWidth = imageWidth * workingScale;
		return realDisplayWidth - this._availbleMaskSize.width;
	}

	private _setImageParallax(progress: number): void {

		return;

		let padding = this._getImageXPadding();
		let padding50 = padding / 2;

		if (!globals.lastCordsFromTouch) {
			let power = 20;
			let powerY = 10;

			this._xParallax.target =
				-padding50 +
				progress * padding +
				(power / 2 -
					(this._mouse.getMouseCords().x / device.innerWidth) * power * this._mousePallaxMultiplier);
			this._yParallax.target =
				(powerY / 2 - (this._mouse.getMouseCords().y / device.innerHeight) * powerY) *
				this._mousePallaxMultiplier;
		} else {
			this._xParallax.target = -padding50 + progress * padding;
		}

		this._xParallax.tick();
		this._yParallax.tick();


		this._$image.x = this._xParallax.value;
		this._$image.y += this._yParallax.value;
		this._$imageEffect.x = this._$image.x;
		this._$imageEffect.y = this._$image.y;
	}

}
